import React, { createContext, useContext, useEffect, useState } from 'react';
import { jobSeekerTranslations } from '../locales';
import { workflowTranslations } from '../workflowLocales';
import {
  getActiveLanguage,
  persistActiveLanguage,
} from '../localePreference';

const RTL_LANGUAGES = ['ar'];

const LanguageContext = createContext(null);

function getValueByPath(source, path) {
  return path.split('.').reduce((current, part) => current?.[part], source);
}

function translationsFor(language) {
  return { ...jobSeekerTranslations[language], ...workflowTranslations[language] };
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getActiveLanguage);

  const isRTL = RTL_LANGUAGES.includes(language);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.lang = language;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.body.classList.toggle('rtl', isRTL);
    document.body.classList.toggle('ltr', !isRTL);

    persistActiveLanguage(language);
  }, [isRTL, language]);

  const changeLanguage = (nextLanguage) => {
    if (!jobSeekerTranslations[nextLanguage]) {
      return false;
    }

    setLanguage(nextLanguage);
    return true;
  };

  const t = (path, fallback = path) => {
    const localized = getValueByPath(translationsFor(language), path);
    if (localized !== undefined) {
      return localized;
    }

    const english = getValueByPath(translationsFor('en'), path);
    return english !== undefined ? english : fallback;
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        isRTL,
        changeLanguage,
        t,
        supportedLanguages: [
          { code: 'en', label: 'EN', fullLabel: 'English' },
          { code: 'ar', label: 'AR', fullLabel: 'العربية' },
        ],
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used inside LanguageProvider');
  }

  return context;
}
